import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon, SearchIcon, UserIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type HeaderProps = {
  language: Language;
  onLanguageChange: (lang: Language) => void;
};
const navItems = {
  ua: ['Каталоги', 'Відео', 'Новини', 'Контакти'],
  en: ['Catalogs', 'Video', 'News', 'Contact']
};
const ctaText = {
  ua: {
    partner: 'Стати партнером'
  },
  en: {
    partner: 'Become a Partner'
  }
};
export function Header({
  language,
  onLanguageChange
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <motion.header initial={{
    y: -100
  }} animate={{
    y: 0
  }} transition={{
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-chamaleon-black/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'}`} data-id="element-113">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-114">
        <div className="flex items-center justify-between h-16 lg:h-20" data-id="element-115">
          {/* Logo */}
          <motion.a href="#" className="flex items-center gap-2 group" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-116">
            <div className="relative flex items-center h-8 sm:h-10" data-id="element-117">
              <img src="https://cdn.magicpatterns.com/uploads/dPC9v62jFFRaqiBcJdHaJe/chamaeleon_logo_white-yellow_RGB_копія.png" alt="CHAMÄLEON" className="h-full w-auto object-contain" data-id="element-118" />
              <div className="absolute inset-0 bg-chamaleon-yellow/20 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-119" />
            </div>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-id="element-120">
            {navItems[language].map((item, index) => <motion.a key={item} href="#" className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors relative group" initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05 + 0.3
          }} data-id="element-121">
                {item}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-chamaleon-yellow group-hover:w-full transition-all duration-300" data-id="element-122" />
              </motion.a>)}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-3" data-id="element-123">
            {/* Language Switcher Dropdown */}
            <div className="hidden sm:block relative" data-id="element-124">
              <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded transition-colors" data-id="element-125">
                {language.toUpperCase()}
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} data-id="element-126" />
              </button>

              <AnimatePresence data-id="element-127">
                {isLangMenuOpen && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} transition={{
                duration: 0.2
              }} className="absolute top-full right-0 mt-2 w-24 bg-chamaleon-charcoal border border-white/10 rounded-lg shadow-xl overflow-hidden z-50" data-id="element-128">
                    {(['en', 'ua'] as Language[]).map(lang => <button key={lang} onClick={() => {
                  onLanguageChange(lang);
                  setIsLangMenuOpen(false);
                }} className={`w-full text-left px-4 py-2 text-sm transition-colors ${language === lang ? 'bg-chamaleon-yellow text-chamaleon-black font-medium' : 'text-gray-300 hover:bg-white/5 hover:text-white'}`} data-id="element-129">
                        {lang.toUpperCase()}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Search and User Icons - Desktop */}
            <div className="hidden md:flex items-center gap-2 mr-2" data-id="element-130">
              <button className="p-2 text-gray-300 hover:text-white transition-colors" data-id="element-131">
                <SearchIcon className="w-5 h-5" data-id="element-132" />
              </button>
              <button className="p-2 text-gray-300 hover:text-white transition-colors" data-id="element-133">
                <UserIcon className="w-5 h-5" data-id="element-134" />
              </button>
            </div>

            {/* Partner CTA - Desktop Only */}
            <motion.a href="#partner" className="hidden md:flex relative px-5 py-2.5 bg-chamaleon-yellow text-chamaleon-black text-sm font-semibold rounded overflow-hidden group" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-135">
              <span className="relative z-10" data-id="element-136">{ctaText[language].partner}</span>
            </motion.a>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-white" aria-label="Toggle menu" data-id="element-137">
              {isMobileMenuOpen ? <XIcon size={24} data-id="element-138" /> : <MenuIcon size={24} data-id="element-139" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-140">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="lg:hidden bg-chamaleon-black/95 backdrop-blur-xl border-t border-white/5" data-id="element-141">
            <nav className="max-w-7xl mx-auto px-4 py-6 space-y-1" data-id="element-142">
              {/* Mobile Search and User */}
              <div className="flex items-center gap-4 px-4 py-3 mb-2 border-b border-white/10" data-id="element-143">
                <button className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors" data-id="element-144">
                  <SearchIcon className="w-5 h-5" data-id="element-145" />
                  <span data-id="element-146">{language === 'ua' ? 'Пошук' : 'Search'}</span>
                </button>
                <div className="w-px h-5 bg-white/10" data-id="element-147" />
                <button className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors" data-id="element-148">
                  <UserIcon className="w-5 h-5" data-id="element-149" />
                  <span data-id="element-150">{language === 'ua' ? 'Профіль' : 'Profile'}</span>
                </button>
              </div>

              {navItems[language].map((item, index) => <motion.a key={item} href="#" className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-chamaleon-charcoal/50 rounded transition-colors" initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} onClick={() => setIsMobileMenuOpen(false)} data-id="element-151">
                  {item}
                </motion.a>)}

              {/* Mobile Language Switcher */}
              <div className="px-4 py-3 border-t border-white/10 mt-2" data-id="element-152">
                <div className="text-gray-500 text-sm mb-2" data-id="element-153">
                  Language / Мова:
                </div>
                <div className="flex items-center gap-2" data-id="element-154">
                  {(['en', 'ua'] as Language[]).map(lang => <button key={lang} onClick={() => {
                onLanguageChange(lang);
                setIsMobileMenuOpen(false);
              }} className={`px-4 py-2 text-sm rounded flex-1 ${language === lang ? 'bg-chamaleon-yellow text-chamaleon-black font-medium' : 'bg-white/5 text-gray-300 border border-white/10'}`} data-id="element-155">
                      {lang.toUpperCase()}
                    </button>)}
                </div>
              </div>

              {/* Mobile Partner CTA */}
              <div className="px-4 py-3 mt-2" data-id="element-156">
                <a href="#partner" className="block w-full text-center px-4 py-3 bg-chamaleon-yellow text-chamaleon-black font-semibold rounded transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-157">
                  {ctaText[language].partner}
                </a>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </motion.header>;
}